// 火山引擎 ARK API 客户端（OpenAI 兼容格式）
// - chatStream(messages, opts)：SSE 流式接口，async generator 逐 chunk yield 文本
// - chatOnce(messages, opts)：一次性请求（内部 collect chatStream），返回完整字符串
// 直连火山引擎方舟平台，无需后端代理
//
// 环境变量：
//   VITE_VOLCANO_API_KEY  — 火山引擎 ARK API Key（ark-xxx 格式）
//   VITE_VOLCANO_MODEL    — 模型ID（如 glm5.2 / doubao-pro-32k / ep-xxxxx）
//   VITE_VOLCANO_BASE_URL — 可选，默认 https://ark.cn-beijing.volces.com/api/v3

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatOptions {
  /** 采样温度，默认 0.7 */
  temperature?: number;
  /** 最大 token 数，默认 400 */
  maxTokens?: number;
  /** 外部传入的取消信号；与内部 30s 超时合并使用 */
  signal?: AbortSignal;
}

/** 流式输出分块类型：区分推理过程和正式回答 */
export interface StreamChunk {
  type: 'reasoning' | 'content';
  text: string;
}

// 默认 60 秒超时（推理模型如 GLM-5.2 需要更多时间）
const REQUEST_TIMEOUT_MS = 60000;

/** 读取 vite 注入的环境变量（构建时已被替换为字面量）
 *  开发环境：通过 Vite 代理 /volcano-api 转发到火山引擎（解决 CORS），密钥在前端可见（仅 dev）
 *  生产环境：通过 /api/ai 代理，密钥由后端注入，不暴露到前端 bundle
 */
function readEnv(): { apiKey: string; model: string; baseUrl: string } {
  const model = (import.meta.env.VITE_VOLCANO_MODEL ?? 'glm5.2').toString();
  if (import.meta.env.DEV) {
    // 开发环境：Vite 代理 /volcano-api，密钥从 .env.local 读取
    const apiKey = (import.meta.env.VITE_VOLCANO_API_KEY ?? '').toString();
    const baseUrl = (import.meta.env.VITE_VOLCANO_BASE_URL ?? '/volcano-api')
      .toString()
      .replace(/\/+$/, '');
    return { apiKey, model, baseUrl };
  }
  // 生产环境：走后端代理 /api/ai，密钥在后端注入，前端不持有
  return { apiKey: '', model, baseUrl: '/api/ai' };
}

/** 当外部 signal 与内部超时 controller 合并：任何一边触发都会 abort */
function linkAbort(external: AbortSignal | undefined, internal: AbortController): () => void {
  if (!external) return () => {};
  if (external.aborted) {
    internal.abort();
    return () => {};
  }
  const onAbort = () => internal.abort();
  external.addEventListener('abort', onAbort);
  return () => external.removeEventListener('abort', onAbort);
}

/**
 * 流式对话接口（SSE）：逐 chunk yield 文本片段
 * 调用方：const gen = chatStream(messages); for await (const text of gen) { ... }
 *
 * 使用火山引擎 ARK OpenAI 兼容接口：
 *   POST {baseUrl}/chat/completions
 *   Header: Authorization: Bearer {apiKey}
 *   Body  : { model, messages, temperature, max_tokens, stream: true }
 *   Resp  : 标准 SSE
 *           data: {"choices":[{"delta":{"content":"..."}}]}\n\n
 *           ...
 *           data: [DONE]\n\n
 */
export async function* chatStream(
  messages: ChatMessage[],
  opts: ChatOptions = {},
): AsyncGenerator<StreamChunk, void, unknown> {
  const { apiKey, model, baseUrl } = readEnv();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const unlink = linkAbort(opts.signal, controller);

  // 开发环境直连火山引擎需要 Authorization header；生产环境由后端代理注入
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (import.meta.env.DEV && apiKey) {
    headers.Authorization = `Bearer ${apiKey}`;
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}/chat/completions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        messages,
        temperature: opts.temperature ?? 0.7,
        max_tokens: opts.maxTokens ?? 400,
        stream: true,
      }),
      signal: controller.signal,
    });
  } catch (err: unknown) {
    clearTimeout(timer);
    unlink();
    const reason = err instanceof Error ? err.message : 'unknown';
    throw new Error(`AI 流式请求失败: ${reason}`);
  }

  if (!response.ok || !response.body) {
    clearTimeout(timer);
    unlink();
    const errText = await response.text().catch(() => '');
    throw new Error(`AI 流式请求失败: HTTP ${response.status} ${errText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE 以空行分隔事件
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || !line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (payload === '[DONE]') return;
        try {
          const obj = JSON.parse(payload);
          const choice = obj?.choices?.[0];
          if (choice) {
            const delta = choice.delta ?? {};
            // GLM-5.2 等推理模型：reasoning_content 是思考过程
            if (typeof delta.reasoning_content === 'string' && delta.reasoning_content.length > 0) {
              yield { type: 'reasoning', text: delta.reasoning_content };
            }
            // 正式回答内容
            if (typeof delta.content === 'string' && delta.content.length > 0) {
              yield { type: 'content', text: delta.content };
            }
            if (obj?.error) {
              throw new Error(`AI 上游错误: ${typeof obj.error === 'string' ? obj.error : JSON.stringify(obj.error)}`);
            }
          }
        } catch (e) {
          if (e instanceof Error && e.message.startsWith('AI 上游错误')) throw e;
        }
      }
    }
  } finally {
    clearTimeout(timer);
    unlink();
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

/** 非流式一次性请求：内部 collect chatStream，只收集 content 类型，返回完整字符串 */
export async function chatOnce(
  messages: ChatMessage[],
  opts: ChatOptions = {},
): Promise<string> {
  let acc = '';
  for await (const chunk of chatStream(messages, opts)) {
    if (chunk.type === 'content') acc += chunk.text;
  }
  return acc;
}
